/**
 * REST client for the public /api/embed/* endpoints.
 *
 * Kept dependency-free (uses fetch) so the bundle stays tiny. Each method
 * captures the network error, normalizes it into a typed result and lets
 * the controller decide how to render the failure state.
 */

import { logger } from "../utils/logger.js";
import type { EmbedErrorDetails } from "../../../shared-types/src/utils/embed-dynamic-variables.js";

/** A session is a call or a written chat, decided when it starts. Wire value of `/session/start`. */
export type SessionModality = "voice" | "text";

export interface EmbedConfigResponse {
  agentId: string;
  branding: { name: string; avatarUrl: string | null };
  widgetSettings: WidgetSettingsPayload;
  allowedDynamicVars: string[];
  requiredDynamicVars?: string[];
  /** Stored greeting guard for permissive button starts; strict uses deployed schema. */
  requiredGreetingVars?: string[];
  languages: string[];
  /**
   * The line the agent opens with, or null when it has none.
   *
   * Shown as the first written bubble before a session exists, so a visitor
   * reads the greeting and decides whether to answer it.
   */
  firstMessage: string | null;
  available: boolean;
  unavailableReason?: string;
}

export type { WebWidgetSettings as WidgetSettingsPayload } from "../../../shared-types/src/types/web-widget-settings.js";
import type { WebWidgetSettings as WidgetSettingsPayload } from "../../../shared-types/src/types/web-widget-settings.js";

interface StartSessionResponse {
  sessionId: string | null;
  modality: SessionModality;
  /** Present for voice sessions (WebRTC). */
  conversationToken?: string;
  /** Present for text sessions (WebSocket). */
  signedUrl?: string;
  expiresInSeconds: number;
  acceptedDynamicVars: string[];
  droppedDynamicVars: string[];
  /** Authoritative values after server allowlisting, coercion and truncation. */
  dynamicVariables?: Record<string, string>;
}

type ApiResult<T> =
  | { ok: true; value: T }
  | {
      ok: false;
      status: number;
      code: string;
      message?: string;
      details?: EmbedErrorDetails;
    };

export class EmbedApiClient {
  constructor(private readonly serverUrl: string) {
    if (!/^https?:\/\//.test(serverUrl)) {
      throw new Error(`EmbedApiClient: invalid serverUrl: ${serverUrl}`);
    }
    // Strip trailing slash so URL concatenation stays predictable.
    this.serverUrl = serverUrl.replace(/\/+$/, "");
  }

  async getConfig(agentId: string): Promise<ApiResult<EmbedConfigResponse>> {
    const url = `${this.serverUrl}/api/embed/config/${encodeURIComponent(agentId)}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        credentials: "omit",
        headers: { Accept: "application/json" },
      });
      const result = await this.parse<EmbedConfigResponse>(response);
      if (!result.ok) return result;
      // Relative assets belong to DeepAgent, never to the customer's website.
      const assetUrl = (url: string | null | undefined): string | null => {
        if (!url?.trim()) return null;
        try {
          return new URL(url, `${this.serverUrl}/`).href;
        } catch {
          return null;
        }
      };
      return {
        ok: true,
        value: {
          ...result.value,
          branding: { ...result.value.branding, avatarUrl: assetUrl(result.value.branding?.avatarUrl) },
          widgetSettings: {
            ...result.value.widgetSettings,
            avatar: { ...result.value.widgetSettings?.avatar, url: assetUrl(result.value.widgetSettings?.avatar?.url) ?? undefined },
          },
        },
      };
    } catch (error) {
      logger.error("getConfig network failure", error);
      return { ok: false, status: 0, code: "network_error" };
    }
  }

  async startSession(input: {
    agentId: string;
    dynamicVariables: Record<string, string>;
    modality: SessionModality;
    variableValidation?: "strict";
  }): Promise<ApiResult<StartSessionResponse>> {
    const url = `${this.serverUrl}/api/embed/session/start?agentId=${encodeURIComponent(input.agentId)}`;
    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          agentId: input.agentId,
          dynamicVariables: input.dynamicVariables,
          modality: input.modality,
          variableValidation: input.variableValidation,
        }),
      });
      return await this.parse<StartSessionResponse>(response);
    } catch (error) {
      logger.error("startSession network failure", error);
      return { ok: false, status: 0, code: "network_error" };
    }
  }

  async endSession(input: {
    sessionId: string | null;
    agentId: string;
    conversationId?: string;
    durationSeconds?: number;
    reason?: "user_ended" | "error" | "timeout";
  }): Promise<void> {
    const url = `${this.serverUrl}/api/embed/session/end`;
    try {
      // Use keepalive so the request still leaves the browser when the visitor
      // closes the tab right after ending the call.
      await fetch(url, {
        method: "POST",
        credentials: "omit",
        keepalive: true,
        // text/plain rather than application/json so this stays a CORS simple
        // request. With application/json the browser preflights, and this
        // endpoint carries no agentId in the URL — so its OPTIONS handler
        // cannot resolve an allowlist, answers without CORS headers, and the
        // browser then blocks the POST. Cross-origin end-of-call markers never
        // arrived; the call sat "in progress" until the webhook caught up.
        // The server reads the body with .json(), which ignores this header.
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: JSON.stringify(input),
      });
    } catch (error) {
      // Fail-soft: the EL webhook is the source of truth for call_history.
      logger.warn("endSession best-effort failed", error);
    }
  }

  private async parse<T>(response: Response): Promise<ApiResult<T>> {
    if (response.ok) {
      const json = (await response.json()) as T;
      return { ok: true, value: json };
    }

    let code = "unknown_error";
    let message: string | undefined;
    let details: EmbedErrorDetails = {};
    try {
      const body = (await response.json()) as EmbedErrorDetails & {
        error?: string;
        message?: string;
      };
      if (body?.error) code = body.error;
      if (body?.message) message = body.message;
      const keys = (value: unknown): string[] | undefined =>
        Array.isArray(value)
          ? value.filter((key): key is string => typeof key === "string")
          : undefined;
      details = {
        missingKeys: keys(body.missingKeys),
        invalidKeys: keys(body.invalidKeys),
        unauthorizedKeys: keys(body.unauthorizedKeys),
        tooLongKeys: keys(body.tooLongKeys),
        reason: typeof body.reason === "string" ? body.reason : undefined,
        retryAfterSeconds: response.headers?.get("Retry-After")
          ? Number(response.headers.get("Retry-After"))
          : undefined,
      };
    } catch {
      // ignore — keep the default error code
    }

    return { ok: false, status: response.status, code, message, details };
  }
}
